import type { InvoiceOverview } from '@vy/api-definition';
import type { CustomFieldValue } from '@vy/custom-fields-types';
import { DEFAULT_CURRENCY, INVOICE_BILLING_STATUSES, INVOICE_TYPES, Period } from '@vy/types';
import {
  formatNumber,
  isEmptyArray,
  isNotEmptyString,
  isNotNullOrUndefined,
  isNullOrUndefined,
  Optional,
  withDefault,
} from '@vy/utils';
import { formatDate } from '../../../../utils/date';
import { findCustomFieldValue, getCustomFieldDisplayName } from '@vy/custom-fields-display';
import { extractUniqueCustomFieldNames } from '../../../../utils/extract-unique-custom-field-names';
import Papa from 'papaparse';
import { INVOICES_COLUMNS_KEYS } from '../invoices-page-components/InvoiceOverviewTable/invoice-overview-table-consts/invoice-columns';
import { INVOICES_PAGE_CSV_HEADERS } from './invoices-page-csv-headers';

type CSVFieldOrder = {
  field: string;
  header: string;
  getValue: (invoice: InvoiceOverview) => string;
};

function formatInvoiceDate(date: Optional<Date>) {
  if (isNullOrUndefined(date)) {
    return '-';
  }

  return formatDate(date, 'MM-DD-YYYY');
}

function formatInvoicePeriod(billingCycle: Period) {
  const start = formatDate(billingCycle.startTime, 'MM-DD-YYYY');
  const end = formatDate(billingCycle.endTime, 'MM-DD-YYYY');
  return `${start} - ${end}`;
}

function getCustomerCrmId(invoice: InvoiceOverview): Optional<string> {
  if (isNotEmptyString(invoice.customer.crmId)) {
    return invoice.customer.crmId;
  }

  return invoice.customer.crmMetadata?.crmId;
}

type CSVFieldOptions = {
  issueOnEndOfMonth: boolean;
};

function getIssueDateValue(inv: InvoiceOverview, options: CSVFieldOptions): string {
  if (options.issueOnEndOfMonth && inv.invoiceType === INVOICE_TYPES.Postpayment) {
    return formatInvoiceDate(inv.billingCycle.endTime);
  }

  return formatInvoiceDate(inv.issuedAt);
}

function getCSVFieldOrder(options: CSVFieldOptions): CSVFieldOrder[] {
  return [
    {
      field: INVOICES_COLUMNS_KEYS.STATUS,
      header: INVOICES_PAGE_CSV_HEADERS.STATUS,
      getValue: (inv) => (inv.billingStatus === INVOICE_BILLING_STATUSES.None ? 'Open' : inv.billingStatus),
    },
    {
      field: 'billingCycle',
      header: INVOICES_PAGE_CSV_HEADERS.DATE,
      getValue: (inv) => formatInvoicePeriod(inv.billingCycle),
    },
    {
      field: INVOICES_COLUMNS_KEYS.CUSTOMER,
      header: INVOICES_PAGE_CSV_HEADERS.CUSTOMER,
      getValue: (inv) => inv.customer.name,
    },
    {
      field: 'customerCrmId',
      header: INVOICES_PAGE_CSV_HEADERS.CUSTOMER_CRM_ID,
      getValue: (inv) => getCustomerCrmId(inv) ?? '-',
    },
    {
      field: 'customerErpId',
      header: INVOICES_PAGE_CSV_HEADERS.CUSTOMER_ERP_ID,
      getValue: (inv) => inv.customer.erpMetadata?.erpId ?? '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.TOTAL_AMOUNT,
      header: INVOICES_PAGE_CSV_HEADERS.AMOUNT,
      getValue: (inv) => formatNumber(inv.revenueBreakdown.total + inv.revenueBreakdown.creditsUsed),
    },
    {
      field: INVOICES_COLUMNS_KEYS.CURRENCY,
      header: INVOICES_PAGE_CSV_HEADERS.CURRENCY,
      getValue: (inv) => inv.currency ?? DEFAULT_CURRENCY,
    },
    {
      field: INVOICES_COLUMNS_KEYS.ID,
      header: INVOICES_PAGE_CSV_HEADERS.INVOICE_NUMBER,
      getValue: (inv) => inv.invoiceNumber ?? '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.ISSUED_AT,
      header: INVOICES_PAGE_CSV_HEADERS.ISSUE_DATE,
      getValue: (inv) => getIssueDateValue(inv, options),
    },
    {
      field: INVOICES_COLUMNS_KEYS.WORKFLOW,
      header: INVOICES_PAGE_CSV_HEADERS.REVIEW_STATUS,
      getValue: (inv) => inv.reviewStatus ?? '-',
    },
    {
      field: 'customerExternalId',
      header: INVOICES_PAGE_CSV_HEADERS.CUSTOMER_EXTERNAL_ID,
      getValue: (inv) => inv.customer.externalId ?? '-',
    },
    {
      field: 'paymentDate',
      header: INVOICES_PAGE_CSV_HEADERS.PAYMENT_DATE,
      getValue: (inv) => formatInvoiceDate(inv.paymentInfo?.date),
    },
    {
      field: INVOICES_COLUMNS_KEYS.CONTRACT_NAME,
      header: INVOICES_PAGE_CSV_HEADERS.CONTRACT,
      getValue: (inv) => inv.contractName ?? '-',
    },
    {
      field: 'invoiceNote',
      header: INVOICES_PAGE_CSV_HEADERS.INVOICE_NOTE,
      getValue: (inv) => inv.paymentInfo?.note ?? '-',
    },
    {
      field: 'transactionId',
      header: INVOICES_PAGE_CSV_HEADERS.TRANSACTION_ID,
      getValue: (inv) => inv.paymentInfo?.transactionId ?? '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.INVOICE_NAME,
      header: 'Period',
      getValue: (inv) => inv.name ?? '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.DUE_DATE,
      header: 'Due Date',
      getValue: (inv) => formatInvoiceDate(inv.dueDate),
    },
    {
      field: INVOICES_COLUMNS_KEYS.LAST_SENT_AT,
      header: 'Last Sent',
      getValue: (inv) =>
        isNotNullOrUndefined(inv.lastSentAt) ? formatDate(inv.lastSentAt, 'MM-DD-YYYY') : '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.CONTRACT_STATUS,
      header: 'Contract Status',
      getValue: (inv) => inv.contractStatus ?? '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.SYNC,
      header: 'Sync',
      getValue: () => '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.MARKETPLACE_UNITS,
      header: INVOICES_PAGE_CSV_HEADERS.MARKETPLACE_UNITS,
      getValue: (inv) =>
        isNotNullOrUndefined(inv.marketplaceUnits) ? formatNumber(inv.marketplaceUnits.total) : '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.MARKETPLACE_SYNC,
      header: INVOICES_PAGE_CSV_HEADERS.MARKETPLACE_SYNC,
      getValue: (inv) => inv.marketplaceSyncStatus?.status ?? '-',
    },
    {
      field: INVOICES_COLUMNS_KEYS.MARKETPLACE_RAW_RESPONSE,
      header: INVOICES_PAGE_CSV_HEADERS.MARKETPLACE_RAW_RESPONSE,
      getValue: (inv) => {
        const raw = inv.marketplaceSyncStatus?.rawResponse;
        return withDefault(raw, '-');
      },
    },
  ];
}

function extractCustomFieldNamesFromVisible(visibleFields: string[], baseFields: Set<string>): string[] {
  return visibleFields.filter((field) => !baseFields.has(field));
}

export function generateCSV(
  invoices: InvoiceOverview[],
  visibleFields?: string[],
  options?: CSVFieldOptions,
) {
  const CSV_FIELD_ORDER = getCSVFieldOrder(options ?? { issueOnEndOfMonth: false });
  const baseFields = new Set(CSV_FIELD_ORDER.map((item) => item.field));
  const customFieldNamesFromData = extractUniqueCustomFieldNames(
    invoices.map((invoice) => invoice.customFieldValues),
  );
  const customFieldNamesFromVisible =
    isNotNullOrUndefined(visibleFields) && !isEmptyArray(visibleFields)
      ? extractCustomFieldNamesFromVisible(visibleFields, baseFields)
      : [];
  const customFieldNames = [...new Set([...customFieldNamesFromData, ...customFieldNamesFromVisible])].sort();
  const fieldOrder = [...CSV_FIELD_ORDER, ...buildCustomFieldOrder(customFieldNames)];
  const fieldsToExport = fieldOrder.map((field) => field.field);
  const headers = generateCSVHeaders(fieldsToExport, fieldOrder);
  const rows = generateCSVRows(invoices, fieldsToExport, fieldOrder);

  return Papa.unparse([headers, ...rows]);
}

function generateCSVHeaders(fieldsToExport: string[], fieldOrder: CSVFieldOrder[]): string[] {
  return fieldOrder.filter((item) => fieldsToExport.includes(item.field)).map((item) => item.header);
}

function generateCSVRows(
  invoices: InvoiceOverview[],
  fieldsToExport: string[],
  fieldOrder: CSVFieldOrder[],
): string[][] {
  return invoices.map((invoice) =>
    fieldOrder.filter((item) => fieldsToExport.includes(item.field)).map((item) => item.getValue(invoice)),
  );
}

function buildCustomFieldOrder(fieldNames: string[]): CSVFieldOrder[] {
  return fieldNames.map((fieldName) => ({
    field: fieldName,
    header: getCustomFieldDisplayName(fieldName),
    getValue: (invoice) => getInvoiceCustomFieldValue(invoice.customFieldValues, fieldName),
  }));
}

function getInvoiceCustomFieldValue(
  customFieldValues: CustomFieldValue[] | null | undefined,
  fieldName: string,
): string {
  const value = findCustomFieldValue(customFieldValues, fieldName);
  if (isNullOrUndefined(value)) {
    return '-';
  }

  return String(value);
}
